import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Users, Bell, Calendar } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SetReminderModal = ({ message, chatId, onClose, onSuccess, chatParticipants = [], isGroupChat = false }) => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [reminderType, setReminderType] = useState('quick'); // 'quick' or 'meeting'
  const [formData, setFormData] = useState({
    meetingTitle: '',
    meetingDateTime: '',
    remindBefore: '15', // minutes before meeting
    notifyGroup: isGroupChat
  });

  // Get quick reminder options
  const quickOptions = [
    { value: '5', label: '5 min' },
    { value: '15', label: '15 min' },
    { value: '30', label: '30 min' },
    { value: '60', label: '1 hour' },
    { value: '120', label: '2 hours' }
  ];

  const remindBeforeOptions = [
    { value: '5', label: '5 minutes before' },
    { value: '15', label: '15 minutes before' },
    { value: '30', label: '30 minutes before' },
    { value: '60', label: '1 hour before' },
    { value: '120', label: '2 hours before' },
    { value: '1440', label: '1 day before' }
  ];

  const handleQuickReminder = async (minutes) => {
    setLoading(true);
    try {
      const remindAt = new Date(Date.now() + parseInt(minutes) * 60 * 1000);
      
      await axios.post(
        `${API}/reminders`,
        {
          message_id: message?.id || null,
          chat_id: chatId,
          remind_at: remindAt.toISOString(),
          reminder_message: message?.content || 'Quick Reminder'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Reminder set for ${minutes} minutes`);
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to set reminder:', error);
      toast.error('Failed to set reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleMeetingReminder = async (e) => {
    e.preventDefault();
    
    if (!formData.meetingTitle.trim()) {
      toast.error('Please enter a meeting title');
      return;
    }
    if (!formData.meetingDateTime) {
      toast.error('Please select meeting date and time');
      return;
    }

    setLoading(true);
    try {
      const meetingTime = new Date(formData.meetingDateTime);
      const remindAt = new Date(meetingTime.getTime() - parseInt(formData.remindBefore) * 60 * 1000);
      
      // If remindAt is in the past, remind immediately
      const finalRemindAt = remindAt < new Date() ? new Date() : remindAt;
      
      const reminderData = {
        message_id: message?.id || null,
        chat_id: chatId,
        remind_at: finalRemindAt.toISOString(),
        reminder_message: `📅 Meeting: ${formData.meetingTitle}\n⏰ Time: ${meetingTime.toLocaleString()}`,
        is_meeting: true,
        meeting_time: meetingTime.toISOString(),
        notify_group: formData.notifyGroup && isGroupChat
      };

      await axios.post(
        `${API}/reminders`,
        reminderData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Meeting reminder set!');
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to set meeting reminder:', error);
      toast.error('Failed to set meeting reminder');
    } finally {
      setLoading(false);
    }
  };

  // Get minimum datetime (now + 1 minute)
  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 1);
    return now.toISOString().slice(0, 16);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className={`rounded-3xl p-6 max-w-md w-full shadow-card ${
            isDarkMode ? 'bg-gray-900' : 'bg-white'
          }`}
          data-testid="reminder-modal"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-[#D4AF37]/20' : 'bg-primary/10'
              }`}>
                <Bell size={20} className="text-[#D4AF37]" />
              </div>
              <h3 className={`font-heading text-xl ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>
                Set Reminder
              </h3>
            </div>
            <button
              onClick={onClose}
              className={`h-8 w-8 rounded-full transition-colors flex items-center justify-center ${
                isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-secondary-DEFAULT text-text-secondary'
              }`}
            >
              <X size={20} />
            </button>
          </div>

          {/* Reminder Type Tabs */}
          <div className={`flex gap-2 p-1 rounded-xl mb-6 ${isDarkMode ? 'bg-gray-800' : 'bg-[#F5F5F7]'}`}>
            <button
              onClick={() => setReminderType('quick')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                reminderType === 'quick'
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white shadow-sm'
                  : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Clock size={16} />
              Quick
            </button>
            <button
              onClick={() => setReminderType('meeting')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                reminderType === 'meeting'
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white shadow-sm'
                  : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Calendar size={16} />
              Meeting
            </button>
          </div>

          {/* Quick Reminder */}
          {reminderType === 'quick' && (
            <div>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>
                Remind me about this in:
              </p>
              <div className="grid grid-cols-3 gap-3">
                {quickOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleQuickReminder(option.value)}
                    disabled={loading}
                    className={`py-3 rounded-xl text-sm font-medium transition-all ${
                      isDarkMode 
                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                        : 'bg-secondary-DEFAULT text-text-primary hover:bg-border-DEFAULT'
                    } disabled:opacity-50`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {message?.content && (
                <div className={`mt-4 p-3 rounded-xl text-sm ${
                  isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-secondary-DEFAULT text-text-secondary'
                }`}>
                  <p className="font-medium mb-1">Message:</p>
                  <p className="line-clamp-2">{message.content}</p>
                </div>
              )}
            </div>
          )}

          {/* Meeting Reminder */}
          {reminderType === 'meeting' && (
            <form onSubmit={handleMeetingReminder} className="space-y-4">
              {/* Meeting Title */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>
                  Meeting Title *
                </label>
                <input
                  type="text"
                  value={formData.meetingTitle}
                  onChange={(e) => setFormData({ ...formData, meetingTitle: e.target.value })}
                  className={`w-full h-12 border-none rounded-xl px-4 focus:ring-1 focus:ring-[#D4AF37] ${
                    isDarkMode 
                      ? 'bg-gray-800 text-white placeholder:text-gray-500' 
                      : 'bg-secondary-DEFAULT text-text-primary'
                  }`}
                  placeholder="Team standup, Client call..."
                  required
                  data-testid="meeting-title-input"
                />
              </div>

              {/* Meeting Date & Time */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>
                  Meeting Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={formData.meetingDateTime}
                  onChange={(e) => setFormData({ ...formData, meetingDateTime: e.target.value })}
                  min={getMinDateTime()}
                  className={`w-full h-12 border-none rounded-xl px-4 focus:ring-1 focus:ring-[#D4AF37] ${
                    isDarkMode 
                      ? 'bg-gray-800 text-white' 
                      : 'bg-secondary-DEFAULT text-text-primary'
                  }`}
                  required
                  data-testid="meeting-datetime-input"
                />
              </div>

              {/* Remind Before */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>
                  Remind Me
                </label>
                <select
                  value={formData.remindBefore}
                  onChange={(e) => setFormData({ ...formData, remindBefore: e.target.value })}
                  className={`w-full h-12 border-none rounded-xl px-4 focus:ring-1 focus:ring-[#D4AF37] ${
                    isDarkMode 
                      ? 'bg-gray-800 text-white' 
                      : 'bg-secondary-DEFAULT text-text-primary'
                  }`}
                  data-testid="remind-before-select"
                >
                  {remindBeforeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Notify Group (if in group chat) */}
              {isGroupChat && (
                <div className={`flex items-center gap-3 p-4 rounded-xl ${
                  isDarkMode ? 'bg-gray-800' : 'bg-secondary-DEFAULT'
                }`}>
                  <input
                    type="checkbox"
                    id="notifyGroup"
                    checked={formData.notifyGroup}
                    onChange={(e) => setFormData({ ...formData, notifyGroup: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="notify-group-checkbox"
                  />
                  <label htmlFor="notifyGroup" className={`flex items-center gap-2 ${
                    isDarkMode ? 'text-gray-300' : 'text-text-primary'
                  }`}>
                    <Users size={18} />
                    <span>Notify all group members</span>
                  </label>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                data-testid="set-meeting-reminder-btn"
              >
                {loading ? 'Setting...' : 'Set Meeting Reminder'}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SetReminderModal;
